import React from 'react';
import { 
  DocumentIcon, 
  PhotoIcon, 
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { formatFileSize, isImageFile } from '../../../utils/attachmentValidation';

/**
 * Component for displaying forum reply attachments
 */
const AttachmentDisplay = ({ attachments, className = '' }) => {
  if (!attachments || !attachments.files || attachments.files.length === 0) {
    return null;
  }

  const handleDownload = (attachment) => {
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = attachment.downloadURL;
    link.download = attachment.originalName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = (attachment) => {
    // Open image in new tab for preview
    window.open(attachment.downloadURL, '_blank', 'noopener,noreferrer');
  };

  const getFileIcon = (attachment) => {
    if (isImageFile(attachment.type)) {
      return <PhotoIcon className="w-5 h-5" />;
    }
    return <DocumentIcon className="w-5 h-5" />;
  };

  const isImage = (attachment) => isImageFile(attachment.type);

  const imageFiles = attachments.files.filter(isImage);
  const nonImageFiles = attachments.files.filter(file => !isImage(file));

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Single Image Display - Show prominently */}
      {imageFiles.length === 1 && (
        <div className="mt-3">
          <div 
            className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer group max-w-lg"
            onClick={() => handlePreview(imageFiles[0])}
          >
            <img
              src={imageFiles[0].downloadURL}
              alt={imageFiles[0].originalName}
              className="w-full h-auto object-contain max-h-96"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 
                          transition-all duration-200 flex items-center justify-center">
              <EyeIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-90 
                               transition-opacity duration-200" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {imageFiles[0].originalName} ({formatFileSize(imageFiles[0].size)})
          </p>
        </div>
      )}

      {/* Show attachment summary only if there are multiple files or non-image files */}
      {(attachments.count > 1 || nonImageFiles.length > 0) && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {attachments.count === 1 ? '1 attachment' : `${attachments.count} attachments`}
          {attachments.totalSize && (
            <span className="ml-2">({formatFileSize(attachments.totalSize)})</span>
          )}
        </div>
      )}

      {/* Attachment List - Show non-image files and multiple files */}
      {((imageFiles.length === 1 && nonImageFiles.length > 0) || imageFiles.length > 1 || imageFiles.length === 0) && (
        <div className="space-y-2">
          {attachments.files
            .filter(attachment => imageFiles.length === 1 ? !isImage(attachment) : true)
            .map((attachment, index) => (
          <div
            key={`${attachment.originalName}-${index}`}
            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            {/* File Icon */}
            <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
              {getFileIcon(attachment)}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {attachment.originalName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(attachment.size)}
                {attachment.uploadedAt && (
                  <span className="ml-2">
                    • {new Date(attachment.uploadedAt).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {isImage(attachment) && (
                <button
                  onClick={() => handlePreview(attachment)}
                  className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 
                           transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Preview image"
                  aria-label={`Preview ${attachment.originalName}`}
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              )}
              
              <button
                onClick={() => handleDownload(attachment)}
                className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 
                         transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Download file"
                aria-label={`Download ${attachment.originalName}`}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
            ))}
        </div>
      )}

      {/* Image Grid for Multiple Images */}
      {attachments.files.filter(isImage).length > 1 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Image Preview
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {attachments.files
              .filter(isImage)
              .slice(0, 6) // Limit to 6 images in grid
              .map((attachment, index) => (
                <button
                  key={`preview-${attachment.originalName}-${index}`}
                  onClick={() => handlePreview(attachment)}
                  className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 
                           hover:opacity-90 transition-opacity group"
                >
                  <img
                    src={attachment.downloadURL}
                    alt={attachment.originalName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 
                                transition-all duration-200 flex items-center justify-center">
                    <EyeIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 
                                     transition-opacity duration-200" />
                  </div>
                </button>
              ))}
            
            {/* Show "and X more" if there are more than 6 images */}
            {attachments.files.filter(isImage).length > 6 && (
              <div className="aspect-square rounded-lg bg-gray-200 dark:bg-gray-700 
                            flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
                +{attachments.files.filter(isImage).length - 6} more
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentDisplay;